import { oc } from "@orpc/contract";
import { z } from "zod";
import { ProductModule } from "@erp/shared-types";

const OrgSummarySchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
});

// A member's membership in one org. `isAdmin` is the per-org admin flag on
// um_users — distinct from the tenant-wide admin flag on um_tenant_users.
const MemberOrgSchema = OrgSummarySchema.extend({
  isAdmin: z.boolean(),
});

// ── tenant.get ──────────────────────────────────────────────

export const GetTenantOutputSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  organizationCount: z.number().int().nonnegative(),
  memberCount: z.number().int().nonnegative(),
});

export const getTenantContract = oc.output(GetTenantOutputSchema).route({
  method: "GET",
  summary: "Get tenant overview",
  description:
    "Returns the current tenant (the company account grouping organizations) with its name, organization count and member count.",
});

// ── tenant.organizations.list ───────────────────────────────

export const ListTenantOrganizationsOutputSchema = z.object({
  organizations: z.array(
    z.object({
      id: z.string().uuid(),
      name: z.string(),
      memberCount: z.number().int().nonnegative(),
    }),
  ),
});

export const listTenantOrganizationsContract = oc
  .output(ListTenantOrganizationsOutputSchema)
  .route({
    method: "GET",
    summary: "List tenant organizations",
    description:
      "Returns every organization in the current tenant with its ID, name and member count.",
  });

// ── tenant.organizations.create ─────────────────────────────

export const CreateTenantOrganizationInputSchema = z.object({
  name: z.string().trim().min(1).max(255),
  // Modules to enable on the new org. The admin chooses these via toggles in the
  // create dialog (pre-filled from the source org); independent of `copyFrom`.
  enabledModules: z.array(z.enum(ProductModule)).default([]),
  // When present, copy the named areas from an existing org in the same tenant.
  copyFrom: z
    .object({
      organizationId: z.string().uuid(),
      logo: z.boolean().default(false),
      azureEmail: z.boolean().default(false),
    })
    .optional(),
});

export const CreateTenantOrganizationOutputSchema = z.object({
  organization: OrgSummarySchema,
});

export const createTenantOrganizationContract = oc
  .input(CreateTenantOrganizationInputSchema)
  .output(CreateTenantOrganizationOutputSchema)
  .route({
    method: "POST",
    summary: "Create an organization in the tenant",
    description:
      "Creates an organization with chosen enabled modules, optionally copying logo and email ingest settings from another org. Adds the creator as org admin.",
  });

// ── tenant.organizations.copyableConfig ─────────────────────

export const CopyableTenantOrganizationConfigInputSchema = z.object({
  organizationId: z.string().uuid(),
});

export const CopyableTenantOrganizationConfigOutputSchema = z.object({
  enabledModules: z.array(z.enum(ProductModule)),
  hasLogo: z.boolean(),
  azureConfigCount: z.number().int().nonnegative(),
});

export const copyableTenantOrganizationConfigContract = oc
  .input(CopyableTenantOrganizationConfigInputSchema)
  .output(CopyableTenantOrganizationConfigOutputSchema)
  .route({
    method: "GET",
    summary: "Get copyable settings from an organization",
    description:
      "Returns what a source organization can seed a new one with: its enabled modules, whether it has a logo, and how many email ingest configurations it has.",
  });

// ── tenant.members.list ─────────────────────────────────────

export const ListTenantMembersOutputSchema = z.object({
  members: z.array(
    z.object({
      tenantUserId: z.string().uuid(),
      name: z.string(),
      isAdmin: z.boolean(),
      deactivatedAt: z.string().nullable(),
      primaryEmail: z.string().nullable(),
      organizations: z.array(MemberOrgSchema),
    }),
  ),
});

export const listTenantMembersContract = oc
  .output(ListTenantMembersOutputSchema)
  .route({
    method: "GET",
    summary: "List tenant members",
    description:
      "Returns every person in the tenant with name, primary email, tenant admin flag, deactivation date, and organization memberships with per-org admin flags.",
  });

// ── tenant.members.add ──────────────────────────────────────

export const AddTenantMemberInputSchema = z.object({
  name: z.string().trim().min(1),
  email: z.string().email(),
  password: z.string().min(6),
  organizationIds: z.array(z.string().uuid()).min(1),
  isAdmin: z.boolean().optional(),
});

export const AddTenantMemberOutputSchema = z.object({
  tenantUserId: z.string().uuid(),
});

export const addTenantMemberContract = oc
  .input(AddTenantMemberInputSchema)
  .output(AddTenantMemberOutputSchema)
  .route({
    method: "POST",
    summary: "Add a person to the tenant",
    description:
      "Creates a new tenant login with name, email and password and adds it to the chosen organizations, optionally as tenant admin. Fails if the email already exists.",
  });

// ── tenant.members.addToOrg ─────────────────────────────────

export const AddTenantMemberToOrgInputSchema = z.object({
  tenantUserId: z.string().uuid(),
  organizationId: z.string().uuid(),
});

export const AddTenantMemberToOrgOutputSchema = z.object({
  userId: z.string().uuid(),
});

export const addTenantMemberToOrgContract = oc
  .input(AddTenantMemberToOrgInputSchema)
  .output(AddTenantMemberToOrgOutputSchema)
  .route({
    method: "POST",
    summary: "Add a tenant member to an organization",
    description:
      "Gives an existing tenant member a user account in another organization of the same tenant. Returns the new organization user ID.",
  });

// ── tenant.members.removeFromOrg ────────────────────────────

export const RemoveTenantMemberFromOrgInputSchema = z.object({
  tenantUserId: z.string().uuid(),
  organizationId: z.string().uuid(),
});

export const RemoveTenantMemberFromOrgOutputSchema = z.object({
  success: z.literal(true),
});

export const removeTenantMemberFromOrgContract = oc
  .input(RemoveTenantMemberFromOrgInputSchema)
  .output(RemoveTenantMemberFromOrgOutputSchema)
  .route({
    method: "POST",
    summary: "Remove a tenant member from an organization",
    description:
      "Deactivates a tenant member's user account in one organization of the tenant; their other memberships are unaffected.",
  });

// ── tenant.members.setAdmin ─────────────────────────────────

export const SetTenantMemberAdminInputSchema = z.object({
  tenantUserId: z.string().uuid(),
  isAdmin: z.boolean(),
});

export const SetTenantMemberAdminOutputSchema = z.object({
  success: z.literal(true),
});

export const setTenantMemberAdminContract = oc
  .input(SetTenantMemberAdminInputSchema)
  .output(SetTenantMemberAdminOutputSchema)
  .route({
    method: "POST",
    summary: "Set tenant admin role",
    description:
      "Grants or revokes tenant-wide admin for a member. Cannot demote the last tenant admin or yourself.",
  });

// ── tenant.members.setOrgAdmin ──────────────────────────────

export const SetTenantMemberOrgAdminInputSchema = z.object({
  tenantUserId: z.string().uuid(),
  organizationId: z.string().uuid(),
  isAdmin: z.boolean(),
});

export const SetTenantMemberOrgAdminOutputSchema = z.object({
  success: z.literal(true),
});

export const setTenantMemberOrgAdminContract = oc
  .input(SetTenantMemberOrgAdminInputSchema)
  .output(SetTenantMemberOrgAdminOutputSchema)
  .route({
    method: "POST",
    summary: "Set organization admin role",
    description:
      "Grants or revokes admin for a tenant member within one of their organizations; separate from the tenant-wide admin role.",
  });

// ── tenant.members.resetPassword ────────────────────────────

export const ResetTenantMemberPasswordInputSchema = z.object({
  tenantUserId: z.string().uuid(),
});

export const ResetTenantMemberPasswordOutputSchema = z.object({
  // Generated server-side and returned exactly once — it is never persisted in
  // plaintext, so there is no endpoint that can read it back.
  temporaryPassword: z.string(),
});

export const resetTenantMemberPasswordContract = oc
  .input(ResetTenantMemberPasswordInputSchema)
  .output(ResetTenantMemberPasswordOutputSchema)
  .route({
    method: "POST",
    summary: "Reset a tenant member's password",
    description:
      "Replaces a member's login password, across all their organizations, with a generated temporary password that is returned once and never stored in plain text.",
  });

// ── Bundle ──────────────────────────────────────────────────

export const tenantContracts = {
  get: getTenantContract,
  organizations: {
    list: listTenantOrganizationsContract,
    create: createTenantOrganizationContract,
    copyableConfig: copyableTenantOrganizationConfigContract,
  },
  members: {
    list: listTenantMembersContract,
    add: addTenantMemberContract,
    addToOrg: addTenantMemberToOrgContract,
    removeFromOrg: removeTenantMemberFromOrgContract,
    setAdmin: setTenantMemberAdminContract,
    setOrgAdmin: setTenantMemberOrgAdminContract,
    resetPassword: resetTenantMemberPasswordContract,
  },
} as const;

export type CreateTenantOrganizationInput = z.infer<
  typeof CreateTenantOrganizationInputSchema
>;
export type CopyableTenantOrganizationConfigOutput = z.infer<
  typeof CopyableTenantOrganizationConfigOutputSchema
>;
export type AddTenantMemberInput = z.infer<typeof AddTenantMemberInputSchema>;
export type ListTenantMembersOutput = z.infer<
  typeof ListTenantMembersOutputSchema
>;
export type ListTenantOrganizationsOutput = z.infer<
  typeof ListTenantOrganizationsOutputSchema
>;
export type GetTenantOutput = z.infer<typeof GetTenantOutputSchema>;
